import { createBrowserRouter, RouteObject } from "react-router";
import dealerRoutes from "../modules/dealer/router";
import accountRoutes from "../modules/account/router";
import { AppRoute } from "../models/AppRoute";
import customerRoutes from "../modules/customer/router";
import { lazy } from "react";
import { DCPPathNames, UserPermissions } from "../utilities/Constants";
import ProtectedRoute from "./ProtectedRoute";
import Signout from "./signout";
import SignoutRedirect from "./SignoutRedirect";
import SigninError from "./signinError";
import GoldErrorBoundaryWrapper from "../components/FallbackUI/GoldErrorBoundaryWrapper";

//#region  Lazy-loaded components
const App = lazy(() => import("../App"));
const Dashboard = lazy(() => import("../modules/dashboard/views/Dashboard"));
const DealershipLandingPageConfiguration = lazy(
    () => import("../modules/dealer/views/DealershipLandingPageConfiguration")
);
const CustomerPreLoginLandingPage = lazy(
    () => import("../modules/customer/views/CustomerPreLoginLandingPage")
);
const CustomerSelfSignupPage = lazy(
    () => import("../modules/customer/views/CustomerSelfSignupPage")
);
//#endregion

const dashboardRoutes: AppRoute[] = [
    {
        path: DCPPathNames.DASHBOARD_PATH,
        name: "Dashboard",
        element: <Dashboard />,
        meta: {
            requiresAuthorization: true,
            authorize: [UserPermissions.MANAGE_CUSTOMER_ACCOUNTS],
            menu: {
                label: "Dashboard",
                icon: ["fas", "chart-line"],
            },
        },
    },
];

const createAppRouter = (userPermissions: string[]) => {
    const baseRoutes: RouteObject[] | any = [
        {
            path: "signout",
            element: <Signout />,
        },
        {
            path: "signout-redirect",
            element: <SignoutRedirect />,
        },
        {
            path: "signin-error",
            element: <SigninError />,
        },
        {
            element: (
                <GoldErrorBoundaryWrapper>
                    <ProtectedRoute userPermissions={userPermissions} />
                </GoldErrorBoundaryWrapper>
            ),
            children: [
                {
                    path: "/",
                    element: <App />,
                },
                {
                    path: DCPPathNames.DEALERSHIP_LANDING_PAGE_PREVIEW_PATH,
                    name: "Dealership Landing Page Preview",
                    element: (
                        <DealershipLandingPageConfiguration isPreview={true} />
                    ),
                },
            ],
        },
        {
            path: DCPPathNames.CUSTOMER_SELF_SIGNUP_PATH,
            name: "Customer Self Signup",
            element: (
                <GoldErrorBoundaryWrapper>
                    <CustomerSelfSignupPage />
                </GoldErrorBoundaryWrapper>
            ),
            meta: {
                requiresAuthorization: false,
            },
        },
        {
            path: DCPPathNames.CUSTOMER_PRE_LOGIN_LANDING_PAGE_PATH,
            name: "Customer Pre Login Landing Page",
            element: (
                <GoldErrorBoundaryWrapper>
                    <CustomerPreLoginLandingPage />
                </GoldErrorBoundaryWrapper>
            ),
            meta: {
                requiresAuthorization: false,
            },
        },
        {
            path: "*",
            element: <GoldErrorBoundaryWrapper />,
        },
    ];

    const routes: AppRoute[] = dashboardRoutes
        .concat(dealerRoutes)
        .concat(accountRoutes)
        .concat(customerRoutes);

    baseRoutes[3].children[0].children = routes;

    return createBrowserRouter(baseRoutes);
};

export default createAppRouter;
